import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { MovesHistory } from "@/types/types"
import { Chess, PieceSymbol, Square } from "chess.js"
import { emptyFen } from "@/constants"
import { getTurn } from "@/utils/turn"
import { Puzzle } from "@/types/serializers"

type ToMove = "w" | "b"

type PuzzleContext = {
  id: string
  title: string | null
  description: string | null
  discoverable: boolean
  draft: boolean
  chess960: boolean
  fen: string
  start: string | null
  resetPosition: () => void
  hasMoves: boolean
  moves: MovesHistory | null
  isEdited: boolean
  toMove: ToMove
  setToMove: (toMove: ToMove) => void
  position: { [key: string]: string }
  setPosition: (position: { [key: string]: string }) => void
  dropSquareStyle: { [key: string]: string }
  setDropSquareStyle: (dropSquareStyle: { [key: string]: string }) => void
}

const PuzzleContext = createContext<PuzzleContext>({} as PuzzleContext)

let isFirstRender = true

export function PuzzleProvider({
  puzzle,
  children,
}: {
  puzzle: Puzzle
  children: ReactNode
}) {
  const { id, title, description, start, moves, discoverable, draft, chess960 = false } = puzzle
  const [fen, setFen] = useState<string>("")
  const [position, setPosition] = useState<{ [key: string]: string }>({})
  const [dropSquareStyle, setDropSquareStyle] = useState<{}>({})
  const [toMove, setToMove] = useState<ToMove>("w")

  const setInitialPosition = useCallback(() => {
    if (!start) return

    try {
      const game = new Chess(undefined, { chess960 })
      game.load(start, { skipValidation: true })
      const board = game.board() // Gets the board as a 2D array
      const position: { [key: string]: string } = {}

      // Iterate over the 2D array
      for (let row = 0; row < board.length; row++) {
        for (let col = 0; col < board[row].length; col++) {
          const square = board[row][col]
          if (square) {
            const file = String.fromCharCode(97 + col) // 'a' + col -> 'a', 'b', 'c', etc.
            const rank = 8 - row // Ranks are inverted
            const squareName = `${file}${rank}`
            const piece = `${square.color}${square.type.toUpperCase()}`
            position[squareName] = piece
          }
        }
      }

      const toMove = getTurn(start)
      setToMove(toMove as ToMove)
      setPosition(position)
    } catch (e) {
      console.log(e)
    }
  }, [start, setPosition])

  useEffect(() => {
    if (!start) return
    setToMove(start.split(" ")[1] as ToMove)
  }, [start])

  useEffect(() => {
    const newFen =
      toMove === "w" ? fen.replace(" b ", " w ") : fen.replace(" w ", " b ")
    setFen(newFen)
  }, [toMove])

  // Set initial position based on stored FEN
  useEffect(() => {
    if (!start) return
    if (!isFirstRender) return

    isFirstRender = false

    setInitialPosition()
  }, [start, setInitialPosition])

  // Set FEN based on current position
  useEffect(() => {
    const gameCopy = new Chess(undefined, { chess960 })
    gameCopy.clear()

    for (const square in position) {
      const piece = position[square]
      const color = piece[0] === "w" ? "w" : "b"
      const type = piece[1].toLowerCase() as PieceSymbol
      gameCopy.put({ type, color }, square as Square)
    }

    const fen = gameCopy.fen()
    const newFen =
      toMove === "w" ? fen.replace(" b ", " w ") : fen.replace(" w ", " b ")
    setFen(newFen)
  }, [position, toMove])

  const hasMoves = fen !== emptyFen
  const isEdited = start !== fen

  return (
    <PuzzleContext.Provider
      value={{
        id,
        title,
        description,
        discoverable,
        draft,
        chess960,
        fen,
        start,
        resetPosition: setInitialPosition,
        hasMoves,
        moves,
        toMove,
        setToMove,
        isEdited,
        position,
        setPosition,
        dropSquareStyle,
        setDropSquareStyle,
      }}
    >
      {children}
    </PuzzleContext.Provider>
  )
}

export function usePuzzle() {
  const context = useContext(PuzzleContext)
  if (context === undefined) {
    throw new Error("usePuzzle must be used within a PuzzleProvider")
  }
  return context
}
